import React from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { CircularProgress, Alert } from '@mui/material'
import { getUserReleases } from '../../api/releases'

const CurrentReleasesSection = () => {
  const uid = localStorage.getItem('uid')

  // Fetch releases data using the same pattern as Releases.jsx
  const { 
    data: releasesData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['current-releases', uid],
    queryFn: () => getUserReleases(uid),
    select: (data) => {
      const mappedReleases = data.data.releases.map(releaseIn => ({
        ...releaseIn,
        status: releaseIn.status.toLowerCase().charAt(0).toUpperCase() + releaseIn.status.toLowerCase().slice(1),
        originalDate: releaseIn.original_date,
        digitalDate: releaseIn.digital_date,
        primaryGenre: releaseIn.primary_genre,
      }))
      return {
        releases: mappedReleases.sort((a, b) => new Date(b.originalDate) - new Date(a.originalDate)).slice(0, 6),
      };
    }
  });

  // Plus icon component
  const PlusIcon = () => (
    <svg 
      width="16" 
      height="16" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <path d="M5 12h14"/>
      <path d="M12 5v14"/>
    </svg>
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="current-releases-section">
        <div className="current-releases-header">
          <h1 className="section-title">Current Releases</h1>
          <Link to="/dashboard/releases/create" className="add-release-btn">
            <PlusIcon />
            Add New Release
          </Link>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '50px 0' }}>
          <CircularProgress size={40} />
        </div>
        <style jsx>{`
          .current-releases-section {
            padding: 24px;
            background: #f8fafc;
            border-radius: 16px;
            height: 100%;
          }
          .current-releases-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 32px;
          }
          .section-title {
            font-size: 24px;
            font-weight: 700;
            color: #111827;
            margin: 0;
          }
          .add-release-btn {
            background: #334155;
            color: white;
            padding: 8px 24px;
            border-radius: 9999px;
            text-decoration: none;
            font-size: 14px;
            font-weight: 500;
            display: flex;
            align-items: center;
            gap: 8px;
            transition: background-color 0.2s ease;
          }
          .add-release-btn:hover {
            background: #1e293b;
          }
        `}</style>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="current-releases-section">
        <div className="current-releases-header">
          <h1 className="section-title">Current Releases</h1>
          <Link to="/dashboard/releases/create" className="add-release-btn">
            <PlusIcon />
            Add New Release
          </Link>
        </div>
        <Alert severity="error" sx={{ margin: '20px 0' }}>
          {error.message || 'Failed to load releases'}
        </Alert>
        <style jsx>{`
          .current-releases-section {
            padding: 24px;
            background: #f8fafc;
            border-radius: 16px;
            height: 100%;
          }
          .current-releases-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 32px;
          }
          .section-title {
            font-size: 24px;
            font-weight: 700;
            color: #111827;
            margin: 0;
          }
          .add-release-btn {
            background: #334155;
            color: white;
            padding: 8px 24px;
            border-radius: 9999px;
            text-decoration: none;
            font-size: 14px;
            font-weight: 500;
            display: flex;
            align-items: center;
            gap: 8px;
            transition: background-color 0.2s ease;
          }
          .add-release-btn:hover {
            background: #1e293b;
          }
        `}</style>
      </div>
    )
  }

  const releases = releasesData?.releases || [];

  return (
    <div className="current-releases-section">
      {/* Header */}
      <div className="current-releases-header">
        <h1 className="section-title">Current Releases</h1>
        <Link to="/dashboard/releases/create" className="add-release-btn">
          <PlusIcon />
          Add New Release
        </Link>
      </div>

      {/* Releases Grid */}
      <div className="releases-grid">
        {releases.length === 0 ? (
          <div className="no-releases">
            <p>No releases found. Create your first release!</p>
          </div>
        ) : (
          releases.map((release) => (
            <div key={release.id} className="release-item">
              <div className="release-image-container">
                <img 
                  src={release.artwork || "/api/placeholder/80/80"} 
                  alt={`${release.title} album cover`} 
                  className="release-artwork" 
                />
              </div>
              <div className="release-details">
                <h3 className="release-name">{release.title}</h3>
                <p className="release-artist">
                  {release.release_artist && release.release_artist.length > 0 
                    ? release.release_artist
                        .filter(artist => artist.role === 'Primary Artist')
                        .map(artist => artist.artist.name)
                        .join(', ') || 'Various Artists'
                    : 'Various Artists'
                  }
                </p>
              </div>
            </div>
          ))
        )}
      </div>

      <style jsx>{`
        .current-releases-section {
          padding: 24px;
          background: #f8fafc;
          border-radius: 16px;
          height: 100%;
        }

        .current-releases-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 32px;
        }

        .section-title {
          font-size: 24px;
          font-weight: 700;
          color: #111827;
          margin: 0;
        }

        .add-release-btn {
          background: #334155;
          color: white;
          padding: 8px 24px;
          border-radius: 9999px;
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: background-color 0.2s ease;
        }

        .add-release-btn:hover {
          background: #1e293b;
        }

        .releases-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 16px;
        }

        @media (min-width: 768px) {
          .releases-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        .release-item {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 10px;
          background: #e2e8f0;
          border-radius: 16px;
          transition: background-color 0.2s ease;
          cursor: pointer;
        }

        .release-item:hover {
          background: #cbd5e1;
        }

        .release-image-container {
          flex-shrink: 0;
        }

        .release-artwork {
          width: 64px;
          height: 64px;
          border-radius: 12px;
          object-fit: cover;
          background: linear-gradient(to bottom right, #60a5fa, #a855f7);
        }

        .release-details {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
         
        }

        .release-name {
          font-size: 16px;
          font-weight: 500;
          color: #111827;
          margin: 0 0 8px 0;
          
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .release-artist {
          font-size: 12px;
          color: #4b5563;
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .no-releases {
          grid-column: 1 / -1;
          text-align: center;
          padding: 40px;
          color: #6b7280;
        }

        @media (max-width: 767px) {
          .current-releases-section {
            padding: 20px;
          }
          
          .current-releases-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
          }
          
          .section-title {
            font-size: 20px;
          }
        }
      `}</style>
    </div>
  )
}

export default CurrentReleasesSection 